import * as React from "react"
import moment from "moment"
import $ from "jquery"
import { getAccessToken, clearAccessToken, waitForAccessToken, setAccessToken } from "./authToken"
import { oktaAuth } from "../auth/oktaConfig"
import { normalizeUserScopes, DEFAULT_SAFE_SCOPES } from "./appPermissions"


/**
 * Returns the int representation of the first argument or the
 * "defaultValue" if the int conversion is not possible.
 * @param {number|string} x The argument to convert
 * @param {number} defaultValue The fall-back return value. This will be
 *                              converted to integer too.
 * @return {Number} The resulting integer.
 */
export function intVal(x, defaultValue = 0) {
    var out = parseInt(x + "", 10);
    if (isNaN(out) || !isFinite(out)) {
        out = intVal(defaultValue);
    }
    return out;
}

export async function sleep(ms) {
    await new Promise((r) => setTimeout(r, ms));
}

/**
 * Converts the argument to boolean value. "0" and "no" are recognized as false.
 * "1" and "yes" are recognized as true. Everything else is just !!var
 * @param {*} x The argument to convert
 * @returns {Boolean} The argument converted to boolean
 */
export function boolVal(x) {
    return (/^(false|0|no)$/i).test(String(x)) ?
        false :
        (/^(true|1|yes)$/i).test(String(x)) ?
            true :
            !!x;
}

/**
 * Given some input argument, this function tries to treat it like an error and
 * extract an error message out of it. Supported input types are:
 * - String
 * - Error
 * - AJAX JSON response
 * - FHIR JSON response
 * - Object map of error messages
 * @param {*} input The input to analyse
 * @param {String|JSX.Element} error(s)
 */
export function getErrorMessage(input) {
    if (input && typeof input == "string") {
        return input;
    }

    let out = "Unknown error"

    if (input && input instanceof Error) {
        out = String(input);
    }

    else if (input && input.responseJSON) {
        if (Array.isArray(input.responseJSON.issue) && input.responseJSON.issue.length) {
            out = input.responseJSON.issue.map(o => o.diagnostics || "-").join("\n")
        }
        else {
            out = (
                input.responseJSON.message ||
                input.responseJSON.error ||
                "Unknown error"
            )
        }
    }

    else if (input && input.responseText) {
        out = (input.responseText + " - " + input.statusText) || "Unknown error"
    }

    else if (input && input.statusText) {
        if (input.statusText == "timeout") {
            out = "The server failed to respond in the desired number of seconds"
        }
        else {
            out = input.statusText || "Unknown error"
        }
    }

    if (out && typeof out == "object") {
        let out2 = []
        for (let key in (out)) {
            out2.push(React.createElement("li", { key }, out[key]));
        }
        return React.createElement("div", null, [
            "Multiple errors",
            React.createElement("ul", null, out2)
        ])
    }

    return String(out).replace(/(Error:\s)+/, "Error: ");
}

/**
 * Walks thru an object (ar array) and returns the value found at the provided
 * path. This function is very simple so it intentionally does not support any
 * argument polymorphism, meaning that the path can only be a dot-separated
 * string. If the path is invalid returns undefined.
 * @param {Object} obj The object (or Array) to walk through
 * @param {String} path The path (eg. "a.b.4.c")
 * @returns {*} Whatever is found in the path or undefined
 */
export function getPath(obj, path = "") {
    return path.split(".").reduce((out, key) => out ? out[key] : undefined, obj)
}

export function compileQueryString(params) {
    let query = []

    for (let key in params) {
        let p = params[key]

        // If the parameter value is falsy (and other than 0) just don't include
        // it in the query
        if (!p && p !== 0) {
            continue;
        }

        // if the value is an array then include that parameter multiple times
        // with different values
        if (Array.isArray(p)) {
            p.forEach(v => {

                // skip falsy not 0 values same as above
                if (v || v === 0) {
                    query.push(
                        encodeURIComponent(key) + "="
                        + encodeURIComponent(v)
                    )
                }
            })
        }

        // Add normal param to the query string
        else {
            query.push(
                encodeURIComponent(key) + "="
                + encodeURIComponent(p)
            )
        }
    }

    return query.join("&")
}

export function parseQueryString(str) {
    let out = {};
    str = String(str || "").trim().split("?").pop();
    str.split(/&/).forEach(pair => {
        let tokens = pair.split("=")
        let key = decodeURIComponent(tokens[0])
        if (key) {
            let value = decodeURIComponent(tokens[1] || "true")
            if (out.hasOwnProperty(key)) {
                if (!Array.isArray(out[key])) {
                    out[key] = [out[key]]
                }
                out[key].push(value)
            }
            else {
                out[key] = value
            }
        }
    })
    return out
}

export function setHashParam(name, value) {
    let query = parseQueryString(location.search.replace(/^\?/, "") || "");
    if (value === undefined) {
        if (query.hasOwnProperty(name)) {
            delete query[name]
        }
    }
    else {
        query[name] = value
    }

    const queryString = compileQueryString(query)
    const newUrl = location.pathname + (queryString ? "?" + queryString : "");
    window.history.replaceState(null, "", newUrl);
}

// Fhir parsing helpers --------------------------------------------------------

/**
 * Given an array of Coding objects finds and returns the one that contains
 * an MRN (using a code == "MR" check)
 * @export
 * @param {Object[]} codings Fhir.Coding[]
 * @returns {Object} Fhir.Coding | undefined
 */
export function findMRNCoding(codings) {
    if (Array.isArray(codings)) {
        return codings.find(coding => coding.code == "MR");
    }
}

/**
 * Given an array of identifier objects finds and returns the one that contains an MRN
 * @export
 * @param {Object[]} identifiers
 * @returns {Object}
 */
// export function findMRNIdentifier(identifiers) {
//     return identifiers.find(
//         identifier => !!findMRNCoding(getPath(identifier, "type.coding"))
//     );
// }
// export function findMRNIdentifier(identifiers) {
//     const mrnIdentifier = identifiers.find(identifier =>
//         typeof identifier.value === "string" && /[a-zA-Z]$/.test(identifier.value) && identifier?.value.length <= 10
//     )
//     if (mrnIdentifier) {
//         return mrnIdentifier;
//     }

//     const otherIdentifier = identifiers.find(identifier =>
//         typeof identifier.value === "string" && identifier?.type?.text === "CLINNEXUS"
//     )

//     if (otherIdentifier) {
//         return otherIdentifier;
//     }

//     return null;
//     // return identifiers.find(identifier =>
//     //     typeof identifier.value === "string" && /[a-zA-Z]$/.test(identifier.value)
//     // );
// }
export function findMRNIdentifier(identifiers) {
    const strategies = [
        // Priority 1: Ends with letter AND length <= 10
        (id) => typeof id.value === "string" && /[a-zA-Z]$/.test(id.value) && id.value.length <= 10,

        // Priority 2: CLINNEXUS type
        (id) => typeof id.value === "string" && id?.type?.text === "CLINNEXUS",

        // Priority 3: CCAHELIG type
        (id) => typeof id.value === "string" && id?.system === "CCAHELIG",

        // Add more strategies here in priority order...
        // (id) => typeof id.value === "string" && id?.type?.text === "SOME_OTHER_TYPE",
    ];

    for (const strategy of strategies) {
        const match = identifiers.find(strategy);
        if (match) return match;
    }

    return null;
}


/**
 * Given a patient returns his MRN
 * @export
 * @param {Object} patient
 * @returns {string}
 */
export function getPatientMRN(patient) {
    // console.log("getPatientMRN patient:", patient);
    let mrn = null;
    if (Array.isArray(patient.identifier) && patient.identifier.length) {
        mrn = findMRNIdentifier(patient.identifier);
        if (mrn) {
            return mrn.value;
        }
    }

    return mrn;
}

export function getPatientDiagnosisReport(group) {
    if (!group?.Condition || !Array.isArray(group.Condition) || group.Condition.length === 0) {
        return <span>N/A</span>;
    }

    // Limit to first 5 records
    const firstFiveConditions = group.Condition.slice(0, 5);

    // Extract diagnosis with coding system info
    const diagnoses = firstFiveConditions
        .map(item => {
            const resource = item.resource;
            if (resource?.code?.coding?.length) {
                const coding = resource.code.coding[0];
                return {
                    code: coding.code || "Unknown Diagnosis",
                    system: coding.system || "" // Get the coding system
                };
            }
            return null;
        })
        .filter(Boolean); // Remove null or undefined

    // Return styled chips based on coding system
    return diagnoses.length > 0 ? diagnoses.map((diagnosis, index) => {
        const isICD = diagnosis.system.includes('icd-10') ||
            diagnosis.system.includes('ICD-10') ||
            diagnosis.system.includes('icd10') ||
            diagnosis.system === 'http://hl7.org/fhir/sid/icd-10';

        const isSNOMED = diagnosis.system.includes('snomed') ||
            diagnosis.system.includes('SNOMED') ||
            diagnosis.system === 'http://snomed.info/sct';

        const chipClass = isICD ? 'diagnosis-chip icd-chip' :
            isSNOMED ? 'diagnosis-chip snomed-chip' :
                'diagnosis-chip unknown-chip';

        return (
            <span key={index} className={chipClass}>
                {diagnosis.code}
            </span>
        );
    }) : "";
}

// export function getICD10DiagnosisReport(group, icd10CodesList) {
//     if (!group?.Condition || !Array.isArray(group.Condition) || group.Condition.length === 0) {
//         return <span>N/A</span>;
//     }

//     // Extract only ICD-10 diagnoses
//     const diagnoses = group.Condition
//         .map(item => {
//             const resource = item.resource;
//             if (resource?.code?.coding?.length) {
//                 const coding = resource.code.coding[0];
//                 const code = coding.code || "Unknown Diagnosis";

//                 // Check if this is an ICD-10 code
//                 const isICD10 = coding.system?.includes('icd-10') ||
//                     coding.system?.includes('ICD-10') ||
//                     coding.system?.includes('icd10') ||
//                     coding.system === 'http://hl7.org/fhir/sid/icd-10' ||
//                     coding.system === 'http://hl7.org/fhir/sid/icd-10-cm' ||
//                     icd10CodesList.includes(code);

//                 if (isICD10) {
//                     return {
//                         code: code,
//                         isValidICD10: icd10CodesList.includes(code)
//                     };
//                 }
//             }
//             return null;
//         })
//         .filter(Boolean);

//     if (diagnoses.length === 0) {
//         return <span>No ICD-10 Diagnoses Found</span>;
//     }

//     // Sort: Valid ICD-10 codes first, then others
//     const sortedDiagnoses = diagnoses.sort((a, b) => {
//         // Valid codes first (true = 1, false = 0, so b - a puts true first)
//         return b.isValidICD10 - a.isValidICD10;
//     });

//     // Take only first 5
//     const firstFive = sortedDiagnoses.slice(0, 5);
//     const hasMore = sortedDiagnoses.length > 5;

//     return (
//         <div className="diagnosis-container">
//             {firstFive.map((diagnosis, index) => {
//                 const chipClass = diagnosis.isValidICD10
//                     ? 'diagnosis-chip icd-chip'
//                     : 'diagnosis-chip snomed-chip';

//                 return (
//                     <span key={index} className={chipClass}>
//                         {diagnosis.code}
//                     </span>
//                 );
//             })}
//             {hasMore && (
//                 <span className="diagnosis-chip more-chip">
//                     +{sortedDiagnoses.length - 5} more
//                 </span>
//             )}
//         </div>
//     );
// }

export function getSDOHReport(group, icd10CodesList) {
    if (!group?.Condition || !Array.isArray(group.Condition) || group.Condition.length === 0) {
        return <span style={{ marginTop: "5px" }}>N/A</span>;
    }

    const seenCodes = new Set();
    const diagnoses = [];

    group.Condition.forEach(item => {
        const resource = item.resource;
        if (resource?.code?.coding?.length) {
            const coding = resource.code.coding[0];
            const code = coding.code || "Unknown Diagnosis";

            // Skip if we've already seen this code or it's not in the list
            if (seenCodes.has(code) || !icd10CodesList.includes(code)) {
                return;
            }

            // Add to diagnoses if it exists in the ICD-10 list
            seenCodes.add(code);
            diagnoses.push({
                code: code,
                isValidICD10: true
            });
        }
    });
    // console.log("SDOH Diagnoses:", diagnoses);

    if (diagnoses.length === 0) {
        return <span>N/A</span>;
    }

    return <ExpandableDiagnosisList diagnoses={diagnoses} />;
}

export function getICD10DiagnosisReport(group, icd10CodesList) {
    if (!group?.Condition || !Array.isArray(group.Condition) || group.Condition.length === 0) {
        return <span style={{ marginTop: "5px" }}>N/A</span>;
    }

    const seenCodes = new Set();
    const diagnoses = [];

    group.Condition.forEach(item => {
        const resource = item.resource;
        if (resource?.code?.coding?.length) {
            const coding = resource.code.coding[0];
            const code = coding.code || "Unknown Diagnosis";

            // Skip if we've already seen this code
            if (seenCodes.has(code)) {
                return;
            }

            // Check if this is an ICD-10 code
            const isICD10 = coding.system?.includes('icd-10') ||
                coding.system?.includes('ICD-10') ||
                coding.system?.includes('icd10') ||
                coding.system === 'http://hl7.org/fhir/sid/icd-10' ||
                coding.system === 'http://hl7.org/fhir/sid/icd-10-cm' ||
                icd10CodesList.includes(code.replace(".", ""));

            if (isICD10) {
                seenCodes.add(code);
                diagnoses.push({
                    code: code,
                    isValidICD10: icd10CodesList.includes(code.replace(".", ""))
                });
            }
        }
    });

    if (diagnoses.length === 0) {
        return <span>N/A</span>;
    }

    // Sort: Valid ICD-10 codes first, then others
    const sortedDiagnoses = diagnoses.sort((a, b) => {
        return b.isValidICD10 - a.isValidICD10;
    });

    return <ExpandableDiagnosisList diagnoses={sortedDiagnoses} />;
}

export function getICD10DiagnosisReportPOF(group, icd10CodesList) {
    if (!group?.Condition || !Array.isArray(group.Condition) || group.Condition.length === 0) {
        return <span style={{ marginTop: "5px" }}>N/A</span>;
    }

    const seenCodes = new Set();
    const diagnoses = [];

    group.Condition.forEach(item => {
        const resource = item.resource;
        if (resource?.code?.coding?.length) {
            const coding = resource.code.coding[0];
            const code = coding.code || "Unknown Diagnosis";

            // Skip if we've already seen this code
            if (seenCodes.has(code)) {
                return;
            }

            // Check if this is an ICD-10 code
            const isICD10 =
                // coding.system?.includes('icd-10') ||
                // coding.system?.includes('ICD-10') ||
                // coding.system?.includes('icd10') ||
                // coding.system === 'http://hl7.org/fhir/sid/icd-10' ||
                coding.system === 'http://hl7.org/fhir/sid/icd-10-cm' ||
                icd10CodesList.includes(code || code.replace(".", ""));

            if (isICD10) {
                seenCodes.add(code);
                diagnoses.push({
                    code: code,
                    isValidICD10: icd10CodesList.includes(code || code.replace(".", ""))
                });
            }
        }
    });

    if (diagnoses.length === 0) {
        return <span style={{ marginTop: "5px" }}>N/A</span>;
    }

    // Sort: Valid ICD-10 codes first, then others
    const sortedDiagnoses = diagnoses.sort((a, b) => {
        return b.isValidICD10 - a.isValidICD10;
    });

    return <ExpandableDiagnosisList diagnoses={sortedDiagnoses} />;
}

// Separate component for expandable functionality
function ExpandableDiagnosisList({ diagnoses }) {
    const [isExpanded, setIsExpanded] = React.useState(false);

    const visibleDiagnoses = isExpanded ? diagnoses : diagnoses.slice(0, 4);
    const hasMore = diagnoses.length > 4;
    const hiddenCount = diagnoses.length - 4;

    return (
        <div className="diagnosis-container">
            {visibleDiagnoses.map((diagnosis, index) => {
                const chipClass = diagnosis.isValidICD10
                    ? 'diagnosis-chip icd-chip'
                    : 'diagnosis-chip snomed-chip';

                return (
                    <span key={index} className={chipClass}>
                        {diagnosis.code}
                    </span>
                );
            })}
            {/* <div className="chips-grid">
            </div> */}

            {hasMore && (
                <button
                    className="expand-button diagnosis-chip snomed-chip"
                    onClick={() => setIsExpanded(!isExpanded)}
                    aria-label={isExpanded ? 'Collapse' : `${hiddenCount} more`}
                >
                    {isExpanded ? (
                        <>
                            <i className="fa fa-chevron-up" aria-hidden="true" style={{ fontSize: "10px" }}></i>
                        </>
                    ) : (
                        <>
                            <i className="fa fa-chevron-down" aria-hidden="true" style={{ fontSize: "10px" }}></i>
                            {hiddenCount} More
                        </>
                    )}
                </button>
            )}
        </div>
    );
}

export const formatSSN = (number, options = {}) => {
    const { maskFirstPart = true, showFull = false } = options;

    if (!number) return '';

    const str = number.toString().replace(/\D/g, '');

    if (str.length !== 9) {
        return showFull ? 'Invalid SSN' : '';
    }

    if (maskFirstPart) {
        return `XXX-XX-${str.slice(-4)}`;
    }

    return `${str.slice(0, 3)}-${str.slice(3, 5)}-${str.slice(5)}`;
};


/**
 * Extracts and returns a human-readable name string from FHIR patient object.
 * @param {Object} patient FHIR patient object
 * @returns {String} Patient's name or an empty string
 */
export function getPatientName(patient) {
    if (!patient) {
        return ""
    }

    let name = patient.name;
    if (!Array.isArray(name)) {
        name = [name];
    }
    name = name[0];
    if (!name) {
        return "";
    }

    let family = Array.isArray(name.family) ? name.family : [name.family];
    let given = Array.isArray(name.given) ? name.given : [name.given];
    let prefix = Array.isArray(name.prefix) ? name.prefix : [name.prefix];
    let suffix = Array.isArray(name.suffix) ? name.suffix : [name.suffix];

    return [
        prefix.map(t => String(t || "").trim()).join(" "),
        given.map(t => String(t || "").trim()).join(" "),
        family.map(t => String(t || "").trim()).join(" "),
        suffix.map(t => String(t || "").trim()).join(" ")
    ].filter(Boolean).join(" ");
}

/**
 * Given a FHIR patient object, returns the patient's phone number (or empty string).
 * Note that if the patient have multiple phones this will only return the first one.
 * @param {Object} patient FHIR patient object
 * @returns {String} Patient's phone or an empty string
 */
export function getPatientPhone(patient = {}) {
    let phone = (patient.telecom || []).find(c => c.system == "phone");
    return phone ? phone.value : "";
}

export function getPHQ(groups = {}, phq) {
    let latestObservation = null;
    let latestDate = null;

    // Look for observation groups by checking if group name includes "Observation"
    for (const groupName in groups) {
        if (groupName.includes("Observation") && Array.isArray(groups[groupName])) {
            for (const entry of groups[groupName]) {
                const resource = entry.resource;

                // Check if this is an Observation resource with the PHQ-9 code
                if (resource.resourceType === "Observation" &&
                    resource.code &&
                    resource.code.coding) {

                    // Look for the PHQ-9 code (44249-1) in the coding array
                    const phqCoding = resource.code.coding.find(coding =>
                        coding.code === "44249-1"
                    );

                    if (phqCoding && resource.valueQuantity && resource.effectiveDateTime) {
                        const currentDate = new Date(resource.effectiveDateTime);

                        // If this is the first valid observation or a newer one, update
                        if (!latestDate || currentDate > latestDate) {
                            latestDate = currentDate;
                            latestObservation = resource;
                        }
                    }
                }
            }
        }
    }

    // Return the value of the latest observation as string, or API fallback / null
    return latestObservation ? String(latestObservation.valueQuantity.value) : phq;
}

/**
 * Given a FHIR patient object, returns the patient's email (or an empty string).
 * Note that if the patient have multiple emails this will only return the first one.
 * @param {Object} patient FHIR patient object
 * @returns {String} Patient's email address or an empty string
 */
export function getPatientEmail(patient = {}) {
    let phone = (patient.telecom || []).find(c => c.system == "email");
    return phone ? phone.value || "" : "";
}

/**
 * Extracts and returns a human-readable address string from FHIR patient object.
 * @param {Object} patient FHIR patient object
 * @returns {String} Patient's address or an empty string
 */
export function getPatientHomeAddress(patient = {}) {
    let a = (patient.address || []);
    a = a.find(c => c.use == "home") || a[0] || {};
    var l = [a.line, a.postalCode, a.city, a.country]
        .map(x => String(x || "").trim())
        .filter(Boolean)
        .join(" ");
    return l ? l : a.text || "";
}

/**
 * Extracts and returns a human-readable age string from FHIR patient object.
 * @param {Object} patient FHIR patient object
 * @returns {String} Patient's age
 */
export function getPatientAge(patient) {
    let from = moment(patient.birthDate || patient);
    let to = moment(patient.deceasedDateTime || undefined);
    let age = to - from;

    let seconds = Math.round(age / 1000)
    if (seconds < 60) {
        return seconds + " second"
    }

    let minutes = Math.round(seconds / 60)
    if (minutes < 60) {
        return minutes + " minute"
    }

    let hours = Math.round(minutes / 60)
    if (hours < 24) {
        return hours + " hour"
    }

    let days = Math.round(hours / 24)
    if (days < 30) {
        return days + " day"
    }

    let months = Math.round(days / 30)
    if (months < 24) {
        return months + " month"
    }

    let years = Math.round(days / 365)
    return years + " year"
}

/**
 * Extracts and returns an URL pointing to the patient photo (or an empty string)
 * @param {Object} patient FHIR patient object
 * @returns {String} Patient's image URL
 */
export function getPatientImageUri(patient, base = "") {
    let data = getPath(patient, "photo.0.data") || "";
    let url = getPath(patient, "photo.0.url") || "";
    let type = getPath(patient, "photo.0.contentType") || "";
    if (url.indexOf("/") === 0) {
        url = base + "" + url;
    }
    let http = url && url.match(/^https?:\/\//);
    if (!http && data) {
        if (type && data.indexOf("data:") !== 0) {
            data = `data:${type};base64,${data}`;
        }
        url = data;
    }
    else if (type && !http) {
        url = `data:${type};base64,${url}`;
    }
    return url;
}

/**
 * Return the display text for the given CodeableConcept
 * @param {Object} concept CodeableConcept
 * @returns {String}
 */
export function getCodeableConcept(concept, defaultValue = "-") {
    return String(
        getPath(concept, "coding.0.display") ||
        getPath(concept, "coding.0.code") ||
        concept?.text ||
        defaultValue
    );
}

/**
 * Some elements are of type `code` in older FHIR versions, but have been
 * converted to CodeableConcept in later versions
 * @param {String|Object} data Code or CodeableConcept
 * @returns {String}
 */
export function getCodeOrConcept(data, defaultValue = "-") {
    if (typeof data == "string") return data || defaultValue;
    return getCodeableConcept(data, defaultValue);
}

/**
 * Given some input string (@input) and a string to search for (@query), this
 * function will highlight all the occurrences by wrapping them in
 * "<span class="search-match"></span>" tag.
 * @param {String} input The input string
 * @param {String} query The string to search for
 * @param {Boolean} caseSensitive Defaults to false
 * @returns {String} The input string with the matches highlighted
 */
export function searchHighlight(input, query, caseSensitive = false) {
    let s = String(input);
    let q = String(query);
    let x = s;

    if (!caseSensitive) {
        x = x.toLowerCase();
        q = q.toLowerCase();
    }

    let i = x.indexOf(q);

    if (i > -1) {
        return (
            s.substr(0, i) +
            `<span class="search-match">` +
            s.substr(i, q.length) +
            "</span>" +
            s.substr(i + q.length)
        );
    }

    return input;
}

/**
 * Uses searchHighlight() to generate and return a JSX.span element containing
 * the @html string with @search highlighted
 * @param {String} html The input string
 * @param {String} search The string to search for
 * @returns {JSX.span} SPAN element with the matches highlighted
 */
export function renderSearchHighlight(html, search) {
    return (
        <span dangerouslySetInnerHTML={{
            __html: search ? searchHighlight(html, search) : html
        }} />
    )
}

/**
 * Given a fhir bundle fins it's link having the given rel attribute.
 * @param {Object} bundle FHIR JSON Bundle object
 * @param {String} rel The rel attribute to look for: prev|next|self... (see
 * http://www.iana.org/assignments/link-relations/link-relations.xhtml#link-relations-1)
 * @returns {String|null} Returns the url of the link or null if the link was
 *                        not found.
 */
export function getBundleURL(bundle, rel) {
    let nextLink = bundle.link;
    if (nextLink) {
        nextLink = nextLink.find(l => l.relation === rel);
        return nextLink && nextLink.url ? nextLink.url : null
    }
    return null;
}

function resolveApiBearerToken(options) {
    if (options.accessToken) {
        return Promise.resolve(options.accessToken);
    }

    return oktaAuth.tokenManager.getTokens()
        .then(tokens => {
            const token = tokens.idToken?.idToken || "";
            if (token) {
                setAccessToken(token);
                localStorage.setItem("accessToken", token);
                localStorage.setItem("idToken", token);
            }
            if (tokens.accessToken?.accessToken) {
                localStorage.setItem("oktaAccessToken", tokens.accessToken.accessToken);
            }
            return token;
        })
        .catch(() => "");
}


export function request(options) {
    options = typeof options == "string" ? { url: options } : options || {};
    const isJson = options.jsonBody === true;
    const isFile = options.responseType === "blob";
    const hasParentWindow = window.parent && window.parent !== window;
    const hasProvidedToken = !!options.accessToken;
    const hasToken = !!getAccessToken();

    const maybeWaitForToken = (hasParentWindow && !hasProvidedToken && !hasToken) ?
        waitForAccessToken(options.tokenWaitTimeout || 5000) :
        Promise.resolve();

    return maybeWaitForToken
        .then(() => resolveApiBearerToken(options))
        .then((bearerToken) => {
        let cfg = $.extend(true, options, {
            timeout: options?.timeout || 60000,
            dataType: options?.dataType || "json",
            headers: {
                Accept: options?.Accept || "application/json",
                Authorization: "Bearer " + bearerToken
            },
        })

        if (isJson && options.data) {
            cfg = {
                ...cfg,
                contentType: "application/json",
                processData: false,
                data: JSON.stringify(options.data)
            };
        }

        if (isFile) {
            cfg = {
                ...cfg,
                dataType: undefined,
                processData: false,
                xhrFields: {
                    responseType: "blob",
                },
            };
        }
        return new Promise((resolve, reject) => {
            $.ajax(cfg).then(
                resolve,
                xhr => {
                    const isAuthFailure = xhr?.status === 401 || xhr?.status === 403;
                    const skipGlobalAuthHandling =
                        options.suppressAuthRedirect;

                    if (isAuthFailure && !skipGlobalAuthHandling) {
                        console.log("Unauthorized - probably invalid access token");
                        clearAccessToken();
                        localStorage.removeItem("accessToken");
                        oktaAuth.tokenManager.clear();
                        const isEmbedded = window.parent && window.parent !== window;
                        if (isEmbedded) {
                            return reject(new Error("Token not passed or invalid"));
                        }
                        if (options.navigate) {
                            options.navigate("/login");
                        } else {
                            window.location.href = "/login";
                        }
                        return;
                    }

                    let message = getErrorMessage(xhr)
                    if (message && typeof message == "string") {
                        return reject(new Error(message))
                    }

                    return reject({ message })
                }
            )
        })
    })
}
export function getResourceTypes(options) {
    // console.log("getAllPages called with options:", options);
    return request(options).then(resource => {
        console.log("Resource received:", resource);
        return resource;
    }).catch(err => {
        console.error("Error in getResourceTypes request:", err);
        throw err; // rethrow so the caller knows
    });;
}

export function postResource(options) {
    return request({
        ...options,
        type: "POST",     // Force POST method
        jsonBody: true    // Ensure JSON body formatting
    })
        .then(response => {
            console.log("POST response received:", response);
            return response;
        })
        .catch(err => {
            console.error("Error in postResource request:", err);
            throw err; // rethrow so the caller can handle it
        });
}

export function postFileResource(options) {
    return request({
        ...options,
        type: "POST",
        jsonBody: false,        // skip JSON.stringify
        processData: false,     // tell jQuery not to process FormData
        contentType: false,     // let browser set multipart boundary automatically
    })
        .then(response => {
            console.log("File upload response:", response);
            return response;
        })
        .catch(err => {
            console.error("Error in postFileResource:", err);
            throw err;
        });
}

export function patchResource(options) {
    return request({
        ...options,
        type: "PATCH",     // Force PATCH method
        jsonBody: true    // Ensure JSON body formatting
    })
        .then(response => {
            console.log("PATCH response received:", response);
            return response;
        })
        .catch(err => {
            console.error("Error in patchResource request:", err);
            throw err; // rethrow so the caller can handle it
        });
}
export function putResource(options) {
    return request({
        ...options,
        type: "PUT",     // Force PUT method
        jsonBody: true    // Ensure JSON body formatting
    })
        .then(response => {
            console.log("PUT response received:", response);
            return response;
        })
        .catch(err => {
            console.error("Error in putResource request:", err);
            throw err; // rethrow so the caller can handle it
        });
}


export function getFhirSearchData(options) {
    // console.log("getAllPages called with options:", options);
    return request(options).then(data => {
        // console.log("FhirSearchData received:", data);
        return data;
    }).catch(err => {
        console.error("Error in getFhirSearchData request:", err);
        throw err; // rethrow so the caller knows
    });;
}

export function getViewPatientData(options) {
    return request(options).then(data => {
        return data;
    }).catch(err => {
        console.error("Error in getViewPatientData request:", err);
        throw err; // rethrow so the caller knows
    });;
}

export function getQuickSightUrl(options) {
    return request(options).then(data => {
        return data;
    }).catch(err => {
        console.error("Error in getQuickSightUrl request:", err);
        throw err; // rethrow so the caller knows
    });;
}

export function getRiskPatientList(options) {
    return request(options).then(data => {
        return data;
    }).catch(err => {
        console.error("Error in getRiskPatientList request:", err);
        throw err; // rethrow so the caller knows
    });;
}

export function getAllPages(options, result = []) {
    return request(options).then(bundle => {
        (bundle.entry || []).forEach(item => {
            if (item.fullUrl && result.findIndex(o => (o.fullUrl === item.fullUrl)) == -1) {
            }
            result.push(item);
        })
        let nextUrl = getBundleURL(bundle, "next");
        if (nextUrl) {
            let encodeUrl = btoa(nextUrl.replace("https://healthlake.us-east-2.amazonaws.com/datastore/f1cda4c3ddd35fdc0ccb4176861c32ed/r4/", ""));
            let updatedUrl = options.server.url + "/everything/nextPage?data=" + encodeUrl;
            return getAllPages({ ...options, url: updatedUrl }, result);
        }
        return result;
    }).catch(err => {
        console.error("Error in getAllPages request:", err);
        throw err; // rethrow so the caller knows
    });;
}

export function getUserRole() {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        return user?.groups || [];
    } catch {
        return [];
    }
}

/**
 * Okta scopes from the user's group description(s).
 * Unknown / empty / invalid scopes fall back to demographics only.
 */
export function getUserPermission() {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        return normalizeUserScopes(user?.scopes || []);
    } catch {
        return [...DEFAULT_SAFE_SCOPES];
    }
}

export function round2(num) {
    if (!num) return 0;
    return Math.round(num * 100) / 100;
};

export function formatMoney(num) {
    if (num == null || num === "") return "-";

    // Round to 2 decimals (avoid floating issues)
    const rounded = Math.round(num * 100) / 100;

    // Convert to string and remove trailing zeros
    const cleaned = rounded.toString().replace(/\.0+$/, "").replace(/(\.\d*[1-9])0+$/, "$1");

    return cleaned;
};

export function formatUSD(num) {
    if (num == null || num === "") return "-";
    return num.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
    })
}

export function getDiagnosisCodes(charge) {
    if (!charge) return "-";

    // Case 1: If Diagnosis or Diagnoses is an array
    if (Array.isArray(charge.Diagnosis)) {
        return charge.Diagnosis.map(d => d.Code).join(", ");
    }
    if (Array.isArray(charge.Diagnoses)) {
        return charge.Diagnoses.map(d => d.Code).join(", ");
    }

    // Case 2: Single object
    return charge?.Diagnosis?.Code
        || charge?.Diagnoses?.Code
        || "-";
};

export function normalizeCharges(charge) {
    if (!charge) return [];
    return Array.isArray(charge) ? charge : [charge];
};

export function formatDate(dateStr) {
    if (!dateStr) return '';

    const date = new Date(dateStr);

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    return `${day} ${month}, ${year}`;
}

export function formatDateTime(dateStr, showTimeOnly = false) {
    if (!dateStr) return '';

    const date = new Date(dateStr);

    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();

    const time = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    });

    if (showTimeOnly) {
        return time;
    }

    return `${day} ${month}, ${year} ${time}`;
}

export function formatDateTimeUTC(dateStr) {
    if (!dateStr) return '';

    const date = new Date(dateStr);

    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
    const year = date.getUTCFullYear();

    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    const seconds = String(date.getUTCSeconds()).padStart(2, '0');

    return `${day} ${month}, ${year} ${hours}:${minutes}:${seconds} UTC`;
}

export function formatDuration(ms) {
    let seconds = Math.floor(ms / 1000);

    if (seconds < 60) {
        return `${seconds} sec`;
    }

    let minutes = Math.floor(seconds / 60);
    seconds = seconds % 60;

    if (minutes < 60) {
        return `${minutes} min ${seconds} sec`;
    }

    let hours = Math.floor(minutes / 60);
    minutes = minutes % 60;

    return `${hours} hr ${minutes} min`;
}

export function formatMonthYear(dateString) {
    if (!dateString) return;
    const date = new Date(dateString);

    return date.toLocaleString('en-US', {
        month: 'short',
        year: 'numeric'
    });
}

export function formatFileSize(bytes) {
    bytes = Number(bytes);

    if (!bytes || isNaN(bytes)) return "0 B";

    const units = ["B", "KB", "MB", "GB", "TB"];
    let index = 0;

    while (bytes >= 1024 && index < units.length - 1) {
        bytes /= 1024;
        index++;
    }

    return `${bytes.toFixed(2)} ${units[index]}`;
}

// /* ─── Helpers ─── */
export const calcAge = (dob) => {
    if (!dob) return "—";
    return Math.floor((Date.now() - new Date(dob).getTime()) / (1000 * 60 * 60 * 24 * 365.25));
};

export const capitalize = (s = "") =>
    s.split(" ").map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

export function statusBadge(s) {
    const map = { Paid: 'badge-green', Pending: 'badge-amber', Denied: 'badge-red', Partial: 'badge-purple', Overdue: 'badge-red', Active: 'badge-green', Expiring: 'badge-amber', Inactive: 'badge-red', Open: 'badge-red', Resolved: 'badge-green', 'In Progress': 'badge-amber' };
    return <span className={`badge ${map[s] || 'badge-gray'}`}>{s}</span>;
}
export function modeBadge(m) {
    const map = { individual: ['badge-cyan', 'individual'], batch: ['badge-purple', 'batch'], 'time-tracked': ['badge-teal', 'time'], 'on-demand': ['badge-amber', 'on-demand'] };
    const [cls] = map[m] || ['badge-gray', m];
    return <span className={`badge ${cls}`}>{m}</span>;
}

export const SkeletonTable = ({ rows, cols }) => (
    <div className="cx-panel" style={{ padding: 0 }}>
        <div className="cx-table-wrap">
            <table className="cx-table">
                <tbody>
                    {Array.from({ length: rows }).map((_, r) => (
                        <tr key={r}>
                            {Array.from({ length: cols }).map((_, c) => (
                                <td key={c}><div className="skeleton-cell" /></td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);
